import React from "react";
import { Users, Package, PlusCircle, MinusCircle, Volume2 } from "lucide-react";
import { playVoice } from "@/lib/tts";

// Deterministically detect a word-problem SITUATION so the child can SEE what is
// happening and decide which sign to use. It only shows the set-up (the given
// quantities and the action) — never the answer, and never names the operation.
export function parseStory(text) {
  if (!text) return null;
  const t = ` ${text.toLowerCase()} `;
  const nums = (text.match(/\d+/g) || []).map(Number);
  if (nums.length < 2) return null;

  // Sharing / dividing equally
  let m = t.match(/(\d+)[^.]*?\b(?:shared?|split|divided?|share)\b[^.]*?\b(?:among|between|into|equally among|equally between)\b[^.]*?(\d+)/);
  if (m) return { kind: "share", total: Number(m[1]), groups: Number(m[2]) };
  m = t.match(/\b(?:shared?|split|divided?)\b[^.]*?(\d+)[^.]*?\b(?:among|between|into)\b[^.]*?(\d+)/);
  if (m) return { kind: "share", total: Number(m[1]), groups: Number(m[2]) };

  // Equal groups (multiplication situation)
  m = t.match(/(\d+)\s+(?:bags?|boxes?|packets?|baskets?|crates?|rows?|groups?|plates?|teams?|shelves|bunches?|jars?|bottles?)\b[^.]*?\b(?:of|each (?:has|hold[s]?|contain[s]?|with))\b[^.]*?(\d+)/);
  if (m) return { kind: "groups", groups: Number(m[1]), each: Number(m[2]) };
  m = t.match(/each[^.]*?\b(?:has|hold[s]?|contain[s]?)\b[^.]*?(\d+)[^.]*?(\d+)\s+(?:bags?|boxes?|rows?|groups?|baskets?)/);
  if (m) return { kind: "groups", groups: Number(m[2]), each: Number(m[1]) };

  // Taking away (subtraction situation)
  if (/\b(gives? away|gave away|sold|ate|eats|used|uses|lost|loses|spends?|spent|removed|takes? away|fewer|how many (?:are )?left|how much (?:is )?left|remain)\b/.test(t))
    return { kind: "takeaway", total: Math.max(nums[0], nums[1]), remove: Math.min(nums[0], nums[1]) };

  // Putting together (addition situation)
  if (/\b(altogether|in all|in total|total number|combined|together|how many (?:are there )?in all)\b/.test(t) || /\band\b[^.]*\bmore\b/.test(t))
    return { kind: "combine", a: nums[0], b: nums[1] };

  return null;
}

const CAP = 12;
const Dots = ({ n, color }) => {
  const show = Math.min(n, CAP);
  return (
    <div className="flex flex-wrap gap-1 justify-center max-w-[120px]">
      {Array.from({ length: show }).map((_, i) => (
        <span key={i} className={`w-2.5 h-2.5 rounded-full ${color}`} />
      ))}
      {n > CAP && <span className="text-xs font-bold text-slate-500">…</span>}
    </div>
  );
};

export default function StoryDiagram({ spec, lang = "en" }) {
  const fr = lang === "fr";
  if (!spec) return null;

  const copy = {
    share: {
      chip: fr ? "On partage en parts égales" : "Sharing equally",
      say: fr
        ? `On partage ${spec.total} en ${spec.groups} parts égales. Combien dans chaque part ?`
        : `We are sharing ${spec.total} equally into ${spec.groups} groups. How many in each group?`,
    },
    groups: {
      chip: fr ? "Des groupes égaux" : "Equal groups",
      say: fr
        ? `Il y a ${spec.groups} groupes, chacun avec ${spec.each}. Combien en tout ?`
        : `There are ${spec.groups} groups, each with ${spec.each}. How many in total?`,
    },
    combine: {
      chip: fr ? "On met ensemble" : "Putting together",
      say: fr ? `On met ${spec.a} et ${spec.b} ensemble.` : `We put ${spec.a} and ${spec.b} together.`,
    },
    takeaway: {
      chip: fr ? "On enlève" : "Taking away",
      say: fr ? `On part de ${spec.total} et on enlève ${spec.remove}.` : `We start with ${spec.total} and take ${spec.remove} away.`,
    },
  }[spec.kind];

  return (
    <div className="mb-3 rounded-2xl border-2 border-slate-900 bg-sky-50 p-3" data-testid="story-diagram">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-300 border-2 border-slate-900 px-2.5 py-0.5 text-xs font-bold text-slate-900">
          {spec.kind === "combine" ? <PlusCircle size={13} /> : spec.kind === "takeaway" ? <MinusCircle size={13} /> : spec.kind === "groups" ? <Package size={13} /> : <Users size={13} />}
          {copy.chip}
        </span>
        <button onClick={() => playVoice(copy.say, lang)} data-testid="story-say-btn"
          className="w-7 h-7 rounded-full bg-teal-500 border-2 border-slate-900 flex items-center justify-center shrink-0" title="Hear this">
          <Volume2 size={13} color="#fff" />
        </button>
      </div>

      {spec.kind === "share" && (
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <div className="text-center"><Dots n={spec.total} color="bg-blue-500" /><div className="text-xs font-bold mt-1">{spec.total}</div></div>
          <span className="text-lg font-black text-slate-500">→</span>
          <div className="flex gap-1.5 flex-wrap justify-center">
            {Array.from({ length: Math.min(spec.groups, 8) }).map((_, i) => (
              <span key={i} className="w-7 h-7 rounded-full border-2 border-slate-900 bg-white flex items-center justify-center"><Users size={13} /></span>
            ))}
            {spec.groups > 8 && <span className="text-xs font-bold text-slate-500 self-center">…{spec.groups}</span>}
          </div>
        </div>
      )}

      {spec.kind === "groups" && (
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {Array.from({ length: Math.min(spec.groups, 6) }).map((_, i) => (
            <span key={i} className="min-w-[2.2rem] px-1.5 py-1 rounded-lg border-2 border-slate-900 bg-white text-center text-xs font-bold">{spec.each}</span>
          ))}
          {spec.groups > 6 && <span className="text-xs font-bold text-slate-500">…{spec.groups} groups</span>}
        </div>
      )}

      {spec.kind === "combine" && (
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <div className="text-center"><Dots n={spec.a} color="bg-blue-500" /><div className="text-xs font-bold mt-1">{spec.a}</div></div>
          <PlusCircle size={18} className="text-green-600" />
          <div className="text-center"><Dots n={spec.b} color="bg-teal-500" /><div className="text-xs font-bold mt-1">{spec.b}</div></div>
        </div>
      )}

      {spec.kind === "takeaway" && (
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <div className="text-center"><Dots n={spec.total} color="bg-blue-500" /><div className="text-xs font-bold mt-1">{spec.total}</div></div>
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 border-2 border-slate-900 px-2 py-0.5 text-xs font-bold text-red-700"><MinusCircle size={13} /> {fr ? "enlève" : "take"} {spec.remove}</span>
        </div>
      )}
    </div>
  );
}
